import React from 'react';
import { darkGreenBarColor, lightGreenBarColor } from '../../theme/xq-custom-theme';
import CustomDivider from '../reusable/CustomDivider';
import HorizontalBarChart from '../reusable/HorizontalBarChart';
import Loader from '../reusable/Loader';
import { calcSelectedTranscriptsDemographicPercentages, createChartConfigs } from './utilityFunctions';
import { getDemographicsSummary } from '../../services/dataQcSummaryService';
import { getDemographicsCategories } from '../../services/eoaConfigServices';
import useSchools from '../hooks/useSchools';
import { TRANSCRIPTS_CATEGORIES } from '../../constants';
import { useTranscriptSelectionOutletContext } from '../hooks/useTranscriptSelectionOutletContext';
import WithDataWrapper from '../reusable/WithDataWrapper';
import type { DemographicsSummary } from '../../interfaces/DataQcInterface';
import { SelectedTranscriptsSummary } from '../../interfaces/TranscriptsInterface';
import { DemographicCategoryOption } from '../../interfaces/EOAInterface';

interface TranscriptSelectionDrawerContentProps {
  demographicsSummary: DemographicsSummary;
  selectedTranscriptsSummary: SelectedTranscriptsSummary;
  demographicCategories: DemographicCategoryOption[];
}

function TranscriptSelectionDrawerContent({
  demographicsSummary,
  selectedTranscriptsSummary,
  demographicCategories,
}: TranscriptSelectionDrawerContentProps) {
  const demographicData = demographicsSummary.total.demographic_percentages;

  const getChartsData = () => {
    if (Object.keys(demographicData).length === 0) return null;

    return createChartConfigs(demographicData, demographicCategories, lightGreenBarColor);
  };

  const getSelectionChartData = () => {
    const highFliersCount =
      selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC].length;
    const onTheCuspCount =
      selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.ON_THE_CUSP].length;
    const strugglingCount =
      selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.STRUGGLING].length;

    if (!highFliersCount && !onTheCuspCount && !strugglingCount) return null;

    const allSelectedTranscripts = [
      ...selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC],
      ...selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.ON_THE_CUSP],
      ...selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.STRUGGLING],
    ];

    const selectedTranscriptsDemographicPercentages = calcSelectedTranscriptsDemographicPercentages(
      demographicsSummary,
      allSelectedTranscripts
    );

    return createChartConfigs(selectedTranscriptsDemographicPercentages, demographicCategories, darkGreenBarColor);
  };

  const chartsData = getChartsData();
  const selectionChartData = getSelectionChartData();

  return (
    <>
      {!chartsData ? <Loader /> : null}
      {chartsData &&
        chartsData.map(({ title, labels, dataSet }, index) => {
          let dataSets = [dataSet];
          if (selectionChartData) {
            dataSets = dataSets.concat(selectionChartData[index].dataSet);
          } else {
            dataSets = dataSets.concat([{ data: new Array(dataSet.data.length).fill(0), backgroundColor: '' }]);
          }
          return (
            <React.Fragment key={index}>
              {index !== 0 && <CustomDivider orientation="horizontal" />}
              <div className="drawer-section in-transcript-selection">
                <p className="section-title in-transcript-selection">{title}</p>
                <div className="transcript-selection-chart-legend">
                  <div className="transcript-selection-chart-legend-label">
                    <div style={{ backgroundColor: 'var(--accent-green-main)' }} className="color-dot"></div>
                    <p>School demographics</p>
                  </div>
                  <div className="transcript-selection-chart-legend-label">
                    <div style={{ backgroundColor: 'var(--accent-green-dark)' }} className="color-dot"></div>
                    <p>Your selection</p>
                  </div>
                </div>
                <div style={{ height: `${labels.length * 60}px` }}>
                  <HorizontalBarChart
                    labels={labels}
                    dataSets={dataSets}
                    customBarPercentage={1}
                    customCategoryPercentage={0.7}
                    customLayoutPaddingRight={50}
                    dataLabelsFormatter={(value) => `${value}%`}
                    maintainAspectRatio={false}
                    afterFit={(axis) => {
                      axis.width = 137;
                    }}
                    max={100}
                    dataLabelsFontSize={12}
                    ticksFontSize={14}
                  />
                </div>
              </div>
            </React.Fragment>
          );
        })}
    </>
  );
}

const TranscriptSelectionDrawerContentWithData = () => {
  const { transcriptSelectionSchool } = useSchools();
  const {
    selectedTranscriptsSummaryResponse: { selectedTranscriptsSummary },
  } = useTranscriptSelectionOutletContext();

  return (
    <WithDataWrapper
      fetchData={async (eoaId: string, access_token: string) => {
        const [demographicsResponse, categoriesResponse] = await Promise.all([
          getDemographicsSummary(eoaId, access_token, transcriptSelectionSchool, false),
          getDemographicsCategories(eoaId, access_token),
        ]);
        return {
          data: {
            demographicsSummary: demographicsResponse.data,
            demographicCategories: categoriesResponse.data,
          },
        };
      }}
      options={{
        dependencies: [transcriptSelectionSchool],
        areDependenciesRequired: true,
      }}
      errorMessage="Demographics data is unavailable!"
      initialState={{
        demographicsSummary: {
          cohort: {
            demographic_percentages: {},
            student_count: 0,
          },
          total: {
            demographic_percentages: {},
            student_count: 0,
          },
        },
        demographicCategories: [],
      }}
    >
      {(data) => (
        <TranscriptSelectionDrawerContent
          demographicsSummary={data.demographicsSummary}
          selectedTranscriptsSummary={selectedTranscriptsSummary}
          demographicCategories={data.demographicCategories}
        />
      )}
    </WithDataWrapper>
  );
};

export default TranscriptSelectionDrawerContentWithData;
